import { useEffect, useState, useCallback } from "react";
import { Plus, X, Power } from "lucide-react";
import { toast } from "sonner";
import {
  adminListInviteCodes, adminCreateInviteCode,
  adminPatchInviteCode, adminDeleteInviteCode,
} from "../../lib/services";
import { formatApiError } from "../../lib/api";

export default function AdminInvites() {
  const [items, setItems] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [draft, setDraft] = useState({ code: "", invitedName: "", invitedEmail: "", usesRemaining: 1 });

  const refresh = useCallback(async () => {
    try {
      const r = await adminListInviteCodes();
      setItems(r.items || []);
    } finally {
      setLoaded(true);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const create = async () => {
    if (!draft.code.trim()) { toast("code required"); return; }
    try {
      await adminCreateInviteCode({
        code: draft.code.trim(),
        invitedName: draft.invitedName.trim() || null,
        invitedEmail: draft.invitedEmail.trim() || null,
        usesRemaining: Number(draft.usesRemaining) || 1,
      });
      toast("invite code created");
      setShowForm(false);
      setDraft({ code: "", invitedName: "", invitedEmail: "", usesRemaining: 1 });
      await refresh();
    } catch (err) {
      toast(formatApiError(err));
    }
  };

  const toggleActive = async (it) => {
    try {
      await adminPatchInviteCode(it.id, { active: !it.active });
      toast(!it.active ? "activated" : "deactivated");
      await refresh();
    } catch (err) {
      toast(formatApiError(err));
    }
  };

  const remove = async (it) => {
    if (!window.confirm("delete this invite code? (only allowed if unused)")) return;
    try {
      await adminDeleteInviteCode(it.id);
      toast("deleted");
      await refresh();
    } catch (err) {
      toast(formatApiError(err));
    }
  };

  return (
    <section data-testid="admin-invites" className="px-5 py-5">
      {!showForm ? (
        <button
          data-testid="admin-invite-new-btn"
          onClick={() => setShowForm(true)}
          className="w-full font-mono text-xs lowercase border-2 border-acid text-acid py-3 hover:bg-acid hover:text-black transition-colors flex items-center justify-center gap-1.5 mb-4"
        >
          <Plus className="w-4 h-4" /> new invite code
        </button>
      ) : (
        <div data-testid="admin-invite-form" className="border-2 border-acid bg-acid/5 p-3 mb-4 space-y-2">
          <input
            data-testid="admin-invite-code"
            value={draft.code}
            onChange={(e) => setDraft({ ...draft, code: e.target.value })}
            placeholder="code (e.g. lobby-marina-2026)"
            className="w-full bg-transparent border-2 border-white/30 px-3 py-2 font-mono text-sm focus:border-acid outline-none"
          />
          <input
            data-testid="admin-invite-name"
            value={draft.invitedName}
            onChange={(e) => setDraft({ ...draft, invitedName: e.target.value })}
            placeholder="invited name (optional)"
            className="w-full bg-transparent border-2 border-white/30 px-3 py-2 font-mono text-sm focus:border-acid outline-none"
          />
          <input
            data-testid="admin-invite-email"
            type="email"
            value={draft.invitedEmail}
            onChange={(e) => setDraft({ ...draft, invitedEmail: e.target.value })}
            placeholder="invited email (optional)"
            className="w-full bg-transparent border-2 border-white/30 px-3 py-2 font-mono text-sm focus:border-acid outline-none"
          />
          <input
            data-testid="admin-invite-uses"
            type="number"
            value={draft.usesRemaining}
            onChange={(e) => setDraft({ ...draft, usesRemaining: e.target.value })}
            placeholder="uses (1 = single, -1 = unlimited)"
            className="w-full bg-transparent border-2 border-white/30 px-3 py-2 font-mono text-sm focus:border-acid outline-none"
          />
          <div className="flex gap-2">
            <button
              data-testid="admin-invite-create-submit"
              onClick={create}
              className="bg-acid text-black font-mono text-sm uppercase tracking-wider px-4 py-2 border-2 border-acid hover:bg-black hover:text-acid transition-colors"
            >
              create
            </button>
            <button
              onClick={() => setShowForm(false)}
              className="font-mono text-xs uppercase tracking-wider text-white/60 hover:text-white px-3 py-2"
            >
              cancel
            </button>
          </div>
        </div>
      )}

      {!loaded ? (
        <p className="font-mono text-xs text-white/40">loading...</p>
      ) : items.length === 0 ? (
        <p className="font-mono text-xs text-white/40">no codes yet.</p>
      ) : (
        <ul className="space-y-2">
          {items.map((it) => {
            const used = it.usedBy || [];
            return (
              <li
                key={it.id}
                data-testid={`admin-invite-${it.id}`}
                className="border-2 border-white/15 p-3"
              >
                <div className="flex items-baseline justify-between gap-2 flex-wrap">
                  <div className="font-mono text-sm text-acid">{it.code}</div>
                  <div className="font-mono text-[9px] uppercase tracking-widest">
                    <span className={it.active ? "text-acid" : "text-white/40"}>
                      {it.active ? "active" : "deactivated"}
                    </span>
                    {" · "}
                    <span className="text-white/60">
                      {it.usesRemaining === -1 ? "unlimited" : `${it.usesRemaining} left`}
                    </span>
                  </div>
                </div>

                {(it.invitedName || it.invitedEmail) && (
                  <div className="font-mono text-[11px] text-white/60 mt-1">
                    {it.invitedName || "—"}{it.invitedEmail ? ` · ${it.invitedEmail}` : ""}
                  </div>
                )}

                {used.length > 0 ? (
                  <ul className="mt-2 space-y-1">
                    {used.map((u, i) => (
                      <li
                        key={u.userId || i}
                        data-testid={`admin-invite-used-${it.id}-${i}`}
                        className="font-mono text-[11px] border-l-2 border-acid pl-2"
                      >
                        <div className="text-white">@{u.username || "?"}</div>
                        <div className="text-white/50 text-[10px]">
                          signed up {(u.signedUpAt || "").slice(0, 10)} ·{" "}
                          {u.logs ?? 0} logs · {u.saves ?? 0} saves ·{" "}
                          last seen {u.lastLoginAt ? u.lastLoginAt.slice(0, 10) : "—"}
                        </div>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <div className="font-mono text-[10px] text-white/30 mt-2">unused</div>
                )}

                <div className="flex gap-2 mt-3">
                  <button
                    data-testid={`admin-invite-toggle-${it.id}`}
                    onClick={() => toggleActive(it)}
                    className="font-mono text-[10px] lowercase border-2 border-white/30 text-white/70 px-2.5 py-1 hover:border-white hover:text-white inline-flex items-center gap-1"
                  >
                    <Power className="w-3 h-3" />
                    {it.active ? "deactivate" : "activate"}
                  </button>
                  {used.length === 0 && (
                    <button
                      data-testid={`admin-invite-delete-${it.id}`}
                      onClick={() => remove(it)}
                      className="font-mono text-[10px] lowercase border-2 border-white/30 text-white/60 px-2.5 py-1 hover:border-white hover:text-white inline-flex items-center gap-1"
                    >
                      <X className="w-3 h-3" />
                      delete
                    </button>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
